import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Output,
  EventEmitter,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-label';
import '@maersk-global/mds-components-core/mc-input';
import { AemsMcModalComponent } from '../../aems-mc-modal/aems-mc-modal.component';
import { AemsFile } from '../../../models/aems-file.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BlobService } from '../../../services/blob.service';
import { ToastrService } from '../../../services/toastr-service';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { AuthPolicies } from '../../../auth/auth-policies';
import { firstValueFrom } from 'rxjs';
import OpenSeadragon from 'openseadragon';
import {
  createOSDAnnotator,
  createBody,
  Color,
  DrawingTool,
  ImageAnnotation,
  OpenSeadragonAnnotator,
  ShapeType,
} from '@annotorious/openseadragon';

type AnnotationFieldKey = 'mode' | 'damageCode' | 'repairCode';

interface AnnotationExportItem {
  id: string;
  shapeType: ShapeType;
  geometry: unknown;
  mode: string;
  damageCode: string;
  repairCode: string;
  color: Color;
}

interface AnnotationExportPayload {
  image: string;
  annotations: AnnotationExportItem[];
}

type AnnotationBody = ImageAnnotation['bodies'][number];

function isColor(value: unknown): value is Color {
  return typeof value === 'string';
}

@Component({
  selector: 'app-file-viewer',
  imports: [AemsMcModalComponent],
  templateUrl: './file-viewer.component.html',
  styleUrl: './file-viewer.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileViewerComponent implements OnChanges, OnDestroy {
  @ViewChild('osdContainer') osdContainer!: ElementRef<HTMLDivElement>;
  private osdViewer: OpenSeadragon.Viewer | null = null;
  private annotator: OpenSeadragonAnnotator | null = null;
  annotationModeEnabled = false;
  submittingAnnotations = false;
  readonly isGSCUser = AuthPolicies.isGSCUser();
  activeDrawingTool: DrawingTool = 'rectangle';
  annotations: ImageAnnotation[] = [];
  readonly annotationFields: { key: AnnotationFieldKey; placeholder: string; maxLength: number }[] = [
    { key: 'mode', placeholder: 'Mode', maxLength: 2 },
    { key: 'damageCode', placeholder: 'Damage code', maxLength: 2 },
    { key: 'repairCode', placeholder: 'Repair code', maxLength: 4 },
  ];
  private readonly annotationFieldPatterns: Record<AnnotationFieldKey, RegExp> = {
    mode: /^\d{2}$/,
    damageCode: /^[A-Z]{2}$/,
    repairCode: /^\d{4}$/,
  };
  // Distinct colors so each annotation's label can be visually tied back to its bounding box
  private readonly annotationColorPalette: Color[] = [
    '#e6194b',
    '#3cb44b',
    '#4363d8',
    '#f58231',
    '#911eb4',
    '#46f0f0',
    '#f032e6',
    '#bcf60c',
    '#fabebe',
    '#008080',
    '#9a6324',
    '#800000',
    '#808000',
    '#000075',
    '#f0a3ff',
  ];
  @Input() navDisplayText = '';
  @Input() openFileViewer = false;
  @Input() disableNext: boolean | null = null;
  @Input() disablePrevious: boolean | null = null;
  @Output() readonly fileViewerClosed = new EventEmitter<boolean>();
  @Output() readonly fileDelete = new EventEmitter<AemsFile>();
  @Output() readonly nextClicked = new EventEmitter();
  @Output() readonly previousClicked = new EventEmitter();
  @Input({ required: true }) file!: AemsFile;
  @Input() readOnly = false;
  deleting: boolean = false;
  @Input() hideActions: boolean = false;
  @Input() showLinkFileIcon: boolean = false;
  @Input() linkFileMessage: string = '';
  // Prefills the Mode field on newly-drawn annotations with the parent work order's repair mode code
  @Input() workOrderMode: string = '';
  @Output() readonly fileLinked = new EventEmitter<AemsFile>();

  sanitizedUrl: SafeResourceUrl | null = null;
  private lastUrl: string = '';
  // The URL actually used to render/derive annotations for; refreshed in the background for
  // persisted files so a long-idle session doesn't keep using an expired SAS signature.
  private resolvedFileUrl: string = '';

  constructor(
    public readonly sanitizer: DomSanitizer,
    private readonly blobService: BlobService,
    private readonly toastrService: ToastrService,
    private readonly appTextSvc: ApplicationtTextService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  // Strict URL validation
  private isSafeUrl(url: string): boolean {
    // Allow only https and blob URLs from trusted domains
    const allowedDomains = ['localhost:4200', 'aems-cdt.maersk.com', 'aems-stage.maersk.com', 'aems.maersk.com'];
    const alloedHostNames = [
      'stgazewcaems001.blob.core.windows.net',
      'stgazewraems001.blob.core.windows.net',
      'stgazewpaems001.blob.core.windows.net',
      'sacazewpmlit001eirimages.blob.core.windows.net',
    ];

    try {
      const parsed = new URL(url);
      const isAllowedHostName = alloedHostNames.some((hostName) => parsed.hostname.includes(hostName));
      return (
        (parsed.protocol === 'https:' || parsed.protocol === 'blob:') &&
        (allowedDomains.some((domain) => parsed.pathname.includes(domain)) || isAllowedHostName)
      );
    } catch {
      return false;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['file'] && this.file) {
      this.onFileChanged();
    }
  }

  private onFileChanged(): void {
    this.destroyOsdViewer();
    if (this.file.fileUrl !== this.lastUrl) {
      this.lastUrl = this.file.fileUrl;
      this.applyFileUrl(this.file.fileUrl);
      void this.refreshResolvedFileUrl();
    }
    if (this.isImageFile(this.file)) {
      // defer until the osdContainer element is rendered
      setTimeout(() => this.initOsdViewer(), 0);
    }
  }

  private applyFileUrl(url: string): void {
    this.resolvedFileUrl = url;
    if (this.isSafeUrl(url)) {
      if (this.isEmbeddableObject(this.file)) {
        this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url); // NOSONAR: URL is validated by isSafeUrl before this point
      } else {
        this.sanitizedUrl = this.sanitizer.bypassSecurityTrustUrl(url); // NOSONAR: URL is validated by isSafeUrl before this point
      }
    } else {
      this.file.isDeleteDisabled = true;
      this.sanitizedUrl = null;
      this.toastrService.showToastr('Unsafe file URL detected.', 'error');
    }
  }

  // Persisted files carry a SAS-signed URL resolved by the backend at some earlier point, which
  // can expire on a long-idle session. blobService.getResourceUrl caches its own SAS with expiry,
  // so re-requesting it here transparently renews the signature without blocking the initial render.
  private async refreshResolvedFileUrl(): Promise<void> {
    if (!this.file.uploadedToBlob || !this.file.shopCode || !this.file.generatedName) return;
    // getResourceUrl only knows how to rebuild URLs for the shop's own container SAS; files served
    // from other storage providers/accounts (e.g. EIR report PDFs) must keep their original URL.
    if (!this.isRefreshableStorageHost(this.file.fileUrl)) return;
    const requestedFor = this.file;
    try {
      const freshUrl = await firstValueFrom(this.blobService.getResourceUrl(this.file.shopCode, this.file.generatedName));
      if (this.file !== requestedFor || !freshUrl || freshUrl === this.resolvedFileUrl) return;
      this.applyFileUrl(freshUrl);
      if (this.isImageFile(this.file)) {
        setTimeout(() => this.initOsdViewer(), 0);
      }
      this.cdr.markForCheck();
    } catch {
      // keep using the already-applied URL; a transient SAS refresh failure shouldn't block viewing
    }
  }

  // Hosts whose blobs live in the shop's own container (addressable by shopCode + generatedName)
  private isRefreshableStorageHost(url: string): boolean {
    const refreshableHostNames = [
      'stgazewcaems001.blob.core.windows.net',
      'stgazewraems001.blob.core.windows.net',
      'stgazewpaems001.blob.core.windows.net',
    ];
    try {
      const hostname = new URL(url).hostname;
      return refreshableHostNames.some((hostName) => hostname.includes(hostName));
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.destroyOsdViewer();
  }

  isImageFile(file: AemsFile): boolean {
    return file?.fileType?.startsWith('image/') || file?.fileType === 'application/image';
  }

  zoomIn(): void {
    this.osdViewer?.viewport.zoomBy(1.5);
  }

  zoomOut(): void {
    this.osdViewer?.viewport.zoomBy(1 / 1.5);
  }

  resetZoom(): void {
    this.osdViewer?.viewport.goHome();
  }

  private initOsdViewer(): void {
    if (!this.osdContainer?.nativeElement || !this.resolvedFileUrl) return;
    this.destroyOsdViewer();
    this.osdViewer = OpenSeadragon({
      element: this.osdContainer.nativeElement,
      tileSources: {
        type: 'image',
        url: this.resolvedFileUrl,
      },
      // Freshly attached files use local blob: URLs (same-origin), but existing files (e.g. read-only
      // review) load from Azure Blob Storage, which is cross-origin. Without crossOriginPolicy, the
      // <img> is fetched without CORS mode, tainting the resulting WebGL texture and making it fail
      // silently ("Error creating texture in WebGL."). Fall back to the canvas drawer too, so the
      // image still renders even if the storage endpoint isn't CORS-enabled.
      crossOriginPolicy: 'Anonymous',
      drawer: ['webgl', 'canvas'],
      // disable built-in controls — icons require asset path config; we provide our own mc-buttons
      showNavigationControl: false,
      showFullPageControl: false,
      minZoomLevel: 0.1,
      maxZoomLevel: 10,
      zoomPerScroll: 1.2,
      // click/double-click must draw & finish annotation shapes, not zoom the viewport
      gestureSettingsMouse: { scrollToZoom: true, clickToZoom: false, dblClickToZoom: false },
      gestureSettingsTouch: { pinchToZoom: true, clickToZoom: false, dblClickToZoom: false },
    });
    // wait for the image to load so the annotator can read the viewer's image bounds
    this.osdViewer.addOnceHandler('open', () => this.initAnnotator());
  }

  // Annotorious tracks shapes in image coordinate space, so annotations stay correctly
  // placed on the image regardless of the current OpenSeadragon zoom/pan state.
  private initAnnotator(): void {
    if (!this.osdViewer) return;
    this.annotator = createOSDAnnotator(this.osdViewer, {
      // Preserve annotate mode across next/previous navigation instead of always starting closed
      drawingEnabled: this.annotationModeEnabled,
      style: (annotation: ImageAnnotation) => {
        const color = this.getAnnotationColor(annotation);
        return { fill: color, fillOpacity: 0.15, stroke: color, strokeWidth: 2 };
      },
    });
    this.annotator.setDrawingTool(this.activeDrawingTool);
    this.annotator.on('createAnnotation', (annotation: ImageAnnotation) => this.onAnnotationCreated(annotation));
    this.annotator.on('updateAnnotation', () => this.refreshAnnotations());
    this.annotator.on('deleteAnnotation', () => this.refreshAnnotations());
    void this.loadExistingAnnotations();
  }

  // Builds the URL of the annotations JSON stored alongside the image in blob storage
  private getAnnotationsUrl(): string | null {
    const baseUrl = this.resolvedFileUrl || this.file?.fileUrl;
    if (!this.file?.uploadedToBlob || !this.file.generatedName || !this.isSafeUrl(baseUrl)) return null;
    try {
      const url = new URL(baseUrl);
      const segments = url.pathname.split('/');
      segments[segments.length - 1] = `${this.file.generatedName}.annotations.json`;
      url.pathname = segments.join('/');
      return url.toString();
    } catch {
      return null;
    }
  }

  private async loadExistingAnnotations(): Promise<void> {
    const url = this.getAnnotationsUrl();
    if (!url || !this.annotator) return;
    const payload = await this.blobService.downloadJsonFile<AnnotationExportPayload>(url);
    if (!payload?.annotations?.length) return;
    this.annotator.setAnnotations(
      payload.annotations.map((item, index) => this.buildAnnotationFromExport(item, index)),
      true,
    );
    this.refreshAnnotations();
  }

  private buildAnnotationFromExport(item: AnnotationExportItem, index: number): ImageAnnotation {
    // Exports made before `id` was persisted (or a blank id) would otherwise all collide on the
    // same key, collapsing every restored shape into one entry that deletes the whole set at once.
    const id = item.id || `restored-annotation-${index}`;
    const bodies = (['mode', 'damageCode', 'repairCode'] as const)
      .filter((key) => item[key])
      .map((key) => createBody(id, { type: 'TextualBody', purpose: key, value: item[key] }));
    return {
      id,
      target: { annotation: id, selector: { type: item.shapeType, geometry: item.geometry } },
      bodies,
      properties: { color: item.color },
    } as unknown as ImageAnnotation;
  }

  private onAnnotationCreated(annotation: ImageAnnotation): void {
    const color = this.annotationColorPalette[Math.floor(Math.random() * this.annotationColorPalette.length)]; // NOSONAR: not security-sensitive, only picks a UI display color
    this.annotator?.updateAnnotation({ ...annotation, properties: { ...annotation.properties, color } });
    this.refreshAnnotations();
  }

  private destroyOsdViewer(): void {
    this.destroyAnnotator();
    if (this.osdViewer) {
      this.osdViewer.destroy();
      this.osdViewer = null;
    }
  }

  private destroyAnnotator(): void {
    this.annotator?.destroy();
    this.annotator = null;
    this.annotations = [];
  }

  toggleAnnotationMode(): void {
    this.annotationModeEnabled = !this.annotationModeEnabled;
    this.annotator?.setDrawingEnabled(this.annotationModeEnabled);
    this.cdr.markForCheck();
  }

  setDrawingTool(tool: DrawingTool): void {
    this.activeDrawingTool = tool;
    this.annotator?.setDrawingTool(tool);
  }

  getAnnotationLabel(annotation: ImageAnnotation): string {
    return this.annotationFields
      .map((field) => this.getAnnotationField(annotation, field.key))
      .filter((value) => value.length > 0)
      .join('-');
  }

  isPolygonAnnotation(annotation: ImageAnnotation): boolean {
    return annotation.target.selector.type === ShapeType.POLYGON;
  }

  getAnnotationColor(annotation: ImageAnnotation): Color {
    const color = annotation.properties?.['color'];
    return isColor(color) ? color : '#0a5da6';
  }

  getAnnotationField(annotation: ImageAnnotation, key: AnnotationFieldKey): string {
    const value = annotation.bodies.find((body: AnnotationBody) => body.purpose === key)?.value;
    // Fall back to the parent work order's mode until the user sets an explicit value, since
    // Annotorious's own drawing/editing updates can otherwise race with a body set on creation
    return value || (key === 'mode' ? this.workOrderMode : '');
  }

  isFieldInvalid(annotation: ImageAnnotation, key: AnnotationFieldKey): boolean {
    const value = this.getAnnotationField(annotation, key);
    return value.length > 0 && !this.annotationFieldPatterns[key].test(value);
  }

  onAnnotationFieldInput(annotation: ImageAnnotation, key: AnnotationFieldKey, event: Event): void {
    if (!this.annotator) return;
    const value = ((event.target as HTMLInputElement)?.value ?? '').toUpperCase();
    const currentBodies = annotation.bodies;
    const existingBody = currentBodies.find((body: AnnotationBody) => body.purpose === key);
    const bodies = existingBody
      ? currentBodies.map((body: AnnotationBody) => (body === existingBody ? { ...body, value } : body))
      : [...currentBodies, createBody(annotation.id, { type: 'TextualBody', purpose: key, value })];
    this.annotator.updateAnnotation({ ...annotation, bodies });
  }

  deleteAnnotationEntry(annotation: ImageAnnotation): void {
    this.annotator?.removeAnnotation(annotation.id);
  }

  private refreshAnnotations(): void {
    this.annotations = this.annotator?.getAnnotations() ?? [];
    this.cdr.markForCheck();
  }

  async submitAnnotations(): Promise<void> {
    if (!this.annotations.length || !this.file.shopCode || this.submittingAnnotations) return;
    const payload: AnnotationExportPayload = {
      image: this.file.displayName,
      annotations: this.annotations.map((annotation) => ({
        id: annotation.id,
        shapeType: annotation.target.selector.type,
        geometry: annotation.target.selector.geometry,
        mode: this.getAnnotationField(annotation, 'mode'),
        damageCode: this.getAnnotationField(annotation, 'damageCode'),
        repairCode: this.getAnnotationField(annotation, 'repairCode'),
        color: this.getAnnotationColor(annotation),
      })),
    };
    // Store alongside the image in the same container using its generated name as the key prefix
    const fileName = `${this.file.generatedName}.annotations.json`;
    this.submittingAnnotations = true;
    this.cdr.markForCheck();
    try {
      await this.blobService.uploadJsonFile(fileName, JSON.stringify(payload, null, 2), this.file.shopCode);
      this.toastrService.showToastr(this.appTextSvc.fileUploadedSuccessfully, 'success');
    } catch {
      this.toastrService.showToastr('Failed to submit annotations', 'warning');
    } finally {
      this.submittingAnnotations = false;
      this.cdr.markForCheck();
    }
  }

  closeFileViewer() {
    this.fileViewerClosed.emit(true);
  }

  onDeleteFileClick() {
    if (this.file.uploadedToBlob) {
      this.deleting = true;
      this.blobService.deleteFile(this.file.generatedName ?? '', this.file.shopCode).subscribe({
        next: () => {
          this.closeFileViewer();
          this.fileDelete.emit(this.file);
          this.toastrService.showToastr(this.appTextSvc.fileDeletedSuccessfully, 'success');
          this.deleting = false;
        },
        error: () => {
          this.toastrService.showToastr(this.appTextSvc.failedToDeleteFile, 'warning');
          this.closeFileViewer();
          this.deleting = false;
        },
      });
    } else {
      this.fileDelete.emit(this.file);
      this.closeFileViewer();
    }
  }
  onNextClicked() {
    this.nextClicked.emit();
  }
  onPreviousClicked() {
    this.previousClicked.emit();
  }

  /**
   * When the user navigates to the first or last file we disable the corresponding `mc-button`,
   * but focus stays inside that disabled button's shadow DOM. The disabled native button then
   * swallows subsequent `Escape` keydown events, so `mc-modal` never fires its `closed` event
   * and the parent never resets `openFileViewer`, leaving the viewer in an unusable state.
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.openFileViewer) {
      this.closeFileViewer();
    }
  }

  onFileLinkedChange(file: AemsFile) {
    file.linkFile = !file.linkFile;
    this.fileLinked.emit(file);
  }

  isEmbeddableObject(file: AemsFile): boolean {
    const embeddableTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    return embeddableTypes.includes(file?.fileType);
  }

  async downloadFile(file: AemsFile): Promise<void> {
    if (!file?.shopCode || !file?.generatedName) {
      this.toastrService.showToastr('Missing file info', 'warning');
      return;
    }
    try {
      const blob = await this.blobService.downloadFile(this.resolvedFileUrl || file.fileUrl, this.file.generatedName);
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.generatedName || file.displayName || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Download failed';
      this.toastrService.showToastr(message, 'warning');
    }
  }
}
